/**
 * Store Selectors
 * Computed values and derived state for efficient rendering
 */

import { useMarketStore } from './marketStore';
import { usePaperTradingStore } from './paperTradingStore';
import { useOrderStore } from './orderStore';
import { brokerSelectors, useConnectedBrokers } from './brokerStore';

// Market selectors
export const useCurrentPrice = () => useMarketStore((state) => state.currentPrice);
export const useConnectionStatus = () => useMarketStore((state) => state.connectionStatus);
export const useMarketStatus = () => useMarketStore((state) => state.marketStatus);
export const useDataMode = () => useMarketStore((state) => state.dataMode);

export const useCandleCount = () => useMarketStore((state) => state.candles.length);
export const useLatestCandle = () => useMarketStore((state) => 
  state.candles.length > 0 ? state.candles[state.candles.length - 1] : null
);

// Paper trading selectors
export const usePaperPosition = () => usePaperTradingStore((state) =>
  state.positions.length > 0 ? state.positions[0] : null
);
export const usePaperBalance = () => usePaperTradingStore((state) => state.balance);
export const usePaperPnL = () => usePaperTradingStore((state) => {
  const { positions, balance, initialBalance } = state;
  const position = positions.length > 0 ? positions[0] : null;
  const unrealizedPnL = position ? position.unrealizedPnL : 0;
  const realizedPnL = balance - initialBalance;
  return { unrealizedPnL, realizedPnL, totalPnL: realizedPnL + unrealizedPnL };
});

export const usePendingOrders = () => usePaperTradingStore((state) => state.pendingOrders);
export const useTradeHistory = () => usePaperTradingStore((state) => state.orders);

// Order selectors
export const useSignalQueue = () => useOrderStore((state) => state.signalQueue);
export const usePendingSignals = () => useOrderStore((state) => 
  state.signalQueue.filter((s) => s.status === 'PENDING')
);

// Live trading selectors (using multi-broker store)
export const useLivePosition = () => {
  const activeBroker = brokerSelectors.useActiveBroker();
  const positions = activeBroker?.positions ?? [];
  return positions.find(p => p.netPos !== 0) ?? null;
};
export const useLiveOrders = () => {
  const activeBroker = brokerSelectors.useActiveBroker();
  return activeBroker?.orders ?? [];
};
export const useLiveConnectionStatus = () => {
  const connectedBrokers = useConnectedBrokers();
  return connectedBrokers.length > 0 ? 'connected' : 'disconnected';
};

// Combined selectors
export const useActivePosition = () => {
  const paperPosition = usePaperTradingStore((state) =>
    state.positions.length > 0 ? state.positions[0] : null
  );
  const livePosition = useLivePosition();
  const dataMode = useMarketStore((state) => state.dataMode);

  if (dataMode === 'live') {
    return livePosition;
  }
  return paperPosition;
};

export const useIsInPosition = () => {
  const paperPosition = usePaperTradingStore((state) =>
    state.positions.length > 0 ? state.positions[0] : null
  );
  const livePosition = useLivePosition();
  const dataMode = useMarketStore((state) => state.dataMode);

  if (dataMode === 'live') {
    return livePosition !== null && livePosition.netPos !== 0;
  }
  return paperPosition !== null;
};

// Performance: Memoized candle data with time filter
export const useCandlesInRange = (startTime: number, endTime: number) => useMarketStore((state) => {
  return state.candles.filter((c) => {
    return c.timestamp >= startTime && c.timestamp <= endTime;
  });
});
